/**
 * Performance by Portfolio - Client Level Breakdown
 * Shows individual portfolio performance within client view
 * Helps identify which portfolios are driving/dragging overall performance
 */

import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

import {
  METRIC_ABSENT,
  TONE_TEXT_CLASS,
  formatMetric,
  isAbsent,
  toneForDirection,
} from '../../../utils/metricFormat';

/** TASK-872's formatter, so this panel spells absence the way every KPI card does. */
const fmtPct = (v: number | null) => formatMetric(v, { kind: 'percent', signed: true });
const isMeasured = (v: number | null | undefined): v is number => !isAbsent(v);

interface PortfolioPerformance {
  id: string;
  name: string;
  /** Null when the return was not computed — never 0. See the ranking note below. */
  ytd: number | null;
  mtd?: number | null;
  contributionToTotal: number | null;
  volatility: number | null;
}

interface PerformanceByPortfolioProps {
  portfolios: PortfolioPerformance[];
  onPortfolioClick?: (portfolioId: string) => void;
  showDetails?: boolean;
}

const PerformanceByPortfolio: React.FC<PerformanceByPortfolioProps> = ({
  portfolios,
  onPortfolioClick,
  showDetails = false
}) => {
  const [expanded, setExpanded] = useState(showDetails);

  // Handle empty portfolios array
  if (!portfolios || portfolios.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-gray-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Performance by Portfolio
            </h3>
            <p className="text-sm text-gray-600">
              Individual portfolio contributions to total performance
            </p>
          </div>
        </div>
        <div className="text-center py-8 text-gray-500">
          <p>No portfolios available for this client.</p>
          <p className="text-sm mt-2">Create a portfolio to see performance breakdown.</p>
        </div>
      </div>
    );
  }

  // TASK-897: a ranking needs something to rank. This panel showed
  //
  //     Best Performer   +0.00%  (green)  Main Investment Portfolio
  //     Worst Performer  +0.00%  (red)    Main Investment Portfolio
  //
  // — identical zeros, colour-coded opposite ways, naming the same portfolio for both. That is
  // the Top Contributors defect (TASK-872) one card lower, and it had the same two causes: the
  // caller coerced null to 0, and this component ranked whatever it was handed.
  //
  // A value that was never computed cannot be ranked against one that was, so it sorts out
  // rather than being treated as the worst.
  const measured = portfolios.filter(
    (p): p is PortfolioPerformance & { ytd: number } => isMeasured(p.ytd),
  );
  const sortedPortfolios = [...measured].sort((a, b) => b.ytd - a.ytd);

  // Best/worst are only meaningful when the extremes DIFFER. With one portfolio, or with
  // several that all returned the same, "best" and "worst" name the same thing and the
  // colours assert a spread that does not exist.
  const spread =
    sortedPortfolios.length > 1 &&
    sortedPortfolios[0].ytd !== sortedPortfolios[sortedPortfolios.length - 1].ytd;
  const bestPerformer = spread ? sortedPortfolios[0] : null;
  const worstPerformer = spread ? sortedPortfolios[sortedPortfolios.length - 1] : null;
  const avgReturn = measured.length
    ? measured.reduce((sum, p) => sum + p.ytd, 0) / measured.length
    : null;
  const unmeasured = portfolios.length - measured.length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Performance by Portfolio
            </h3>
            <p className="text-sm text-gray-600">
              Individual portfolio contributions to total performance
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          {expanded ? (
            <>
              Hide Details <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              Show Details <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className={`rounded-lg p-4 border ${bestPerformer
          ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
          data-testid="perf-best">
          <div className="text-sm text-gray-600 mb-1">Best Performer</div>
          <div className={`text-lg font-bold ${bestPerformer
            ? 'text-green-600' : 'text-gray-400'}`}>
            {bestPerformer ? fmtPct(bestPerformer.ytd) : METRIC_ABSENT}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {bestPerformer ? bestPerformer.name : 'no spread to rank'}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Average Return</div>
          <div className={`text-lg font-bold ${TONE_TEXT_CLASS[toneForDirection(avgReturn, 2)]}`}
               data-testid="perf-average">
            {fmtPct(avgReturn)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Across {measured.length} portfolio{measured.length === 1 ? '' : 's'}
            {unmeasured > 0 && ` · ${unmeasured} without a return`}
          </div>
        </div>

        <div className={`rounded-lg p-4 border ${worstPerformer
          ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
          data-testid="perf-worst">
          <div className="text-sm text-gray-600 mb-1">Worst Performer</div>
          <div className={`text-lg font-bold ${worstPerformer
            ? 'text-red-600' : 'text-gray-400'}`}>
            {worstPerformer ? fmtPct(worstPerformer.ytd) : METRIC_ABSENT}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {worstPerformer ? worstPerformer.name : 'no spread to rank'}
          </div>
        </div>
      </div>

      {/* Portfolio List */}
      <div className="space-y-3">
        {sortedPortfolios.map((portfolio, index) => (
          <div
            key={portfolio.id}
            // Above/below average is only a distinction when there IS an average to be above.
            className={`border-l-4 pl-4 py-3 rounded-r-lg transition-colors ${
              avgReturn === null
                ? 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                : portfolio.ytd >= avgReturn
                  ? 'border-green-500 bg-green-50 hover:bg-green-100'
                  : 'border-yellow-500 bg-yellow-50 hover:bg-yellow-100'
            } ${onPortfolioClick ? 'cursor-pointer' : ''}`}
            onClick={() => onPortfolioClick?.(portfolio.id)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-0.5 rounded">
                    #{index + 1}
                  </span>
                  <span className="font-medium text-gray-900">{portfolio.name}</span>
                </div>

                {expanded && (
                  <div className="flex gap-6 mt-2 text-sm">
                    {/* Each figure follows its OWN value — TASK-872's rule. An absent one is
                        a dash in grey, not a green zero. */}
                    <div>
                      <span className="text-gray-600">Contribution:</span>
                      <span className={`ml-2 font-semibold ${
                        TONE_TEXT_CLASS[toneForDirection(portfolio.contributionToTotal, 2)]
                      }`}>
                        {fmtPct(portfolio.contributionToTotal)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Volatility:</span>
                      <span className="ml-2 font-semibold text-gray-900">
                        {formatMetric(portfolio.volatility, { kind: 'percent' })}
                      </span>
                    </div>
                    {portfolio.mtd !== undefined && (
                      <div>
                        <span className="text-gray-600">MTD:</span>
                        <span className={`ml-2 font-semibold ${
                          TONE_TEXT_CLASS[toneForDirection(portfolio.mtd ?? null, 2)]
                        }`}>
                          {fmtPct(portfolio.mtd ?? null)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="text-right ml-4">
                <div className="flex items-center gap-2">
                  {portfolio.ytd >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`text-xl font-bold ${
                    portfolio.ytd >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {portfolio.ytd >= 0 ? '+' : ''}{portfolio.ytd.toFixed(2)}%
                  </span>
                </div>
                {!expanded && (
                  <div className="text-xs text-gray-500 mt-1">
                    YTD Return
                  </div>
                )}
              </div>
            </div>

            {/* Performance Bar */}
            {expanded && (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-16">Impact:</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 relative overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        isMeasured(portfolio.contributionToTotal)
                          ? (portfolio.contributionToTotal >= 0 ? 'bg-green-500' : 'bg-red-500')
                          : 'bg-gray-300'
                      }`}
                      style={{
                        width: isMeasured(portfolio.contributionToTotal)
                          ? `${Math.min(Math.abs(portfolio.contributionToTotal) * 20, 100)}%`
                          : '0%'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Insight. Only when there IS a spread — a sentence about dispersion over one
          portfolio, or over several that all returned the same, describes the arithmetic
          rather than the book. */}
      {bestPerformer && worstPerformer && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-800" data-testid="perf-insight">
            <strong>Analysis:</strong> {
              bestPerformer.ytd - worstPerformer.ytd > 5
                ? `Significant performance dispersion (${(bestPerformer.ytd - worstPerformer.ytd).toFixed(1)}% range). Consider reviewing underperforming portfolios for rebalancing opportunities.`
                : `Consistent performance across portfolios (${(bestPerformer.ytd - worstPerformer.ytd).toFixed(1)}% range). Portfolio strategies are well-aligned.`
            }
          </p>
        </div>
      )}

      {onPortfolioClick && (
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Click on any portfolio to view detailed analysis
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceByPortfolio;
